import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Form, Offcanvas, Table, Spinner, Modal } from 'react-bootstrap';
import {
  XCircleFill,
  CheckCircleFill,
  Trash,
  GeoAltFill,
  PlusCircleFill,
  PencilSquare,
  Search,
  Map
} from 'react-bootstrap-icons';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography
} from "@mui/material";
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import API_BASE_URL from '../../../config/api';
import { getAdminAuthHeader } from '../../../services/auth';

const LOCATION_API_BASE = `${API_BASE_URL}/api/location`;
const LOCATION_LIST_API = `${LOCATION_API_BASE}/admin/getAllLocations`;

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{ color, lineHeight: 1, opacity: disabled ? 0.4 : 1, minWidth: 'auto' }}
      >
        {children}
      </Button>
    </span>
  </OverlayTrigger>
);

// Map settings
const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px',
  border: '1.5px solid #dde4f5'
};

const center = {
  lat: 20.5937, // Default center at India
  lng: 78.9629
};

function loadGoogleMapsScript({ apiKey }) {
  if (window._mapsPromise) return window._mapsPromise;
  window._mapsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector("script[data-google-maps]");
    if (existing) return resolve(window.google.maps);

    const script = document.createElement("script");
    script.setAttribute("data-google-maps", "true");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error("Google Maps load failed"));
    document.head.appendChild(script);
  });
  return window._mapsPromise;
}

const Data = () => {
  const [showCanvas, setShowCanvas] = useState(false);
  const [formData, setFormData] = useState({ location: '', latitude: '', longitude: '' });
  const [dataList, setDataList] = useState([]);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [map, setMap] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteService = useRef(null);
  const geocoderService = useRef(null);
  const inputRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyAklz8Mhdgjy3jrxLjp6BJUZE83yYFR8PY" || '',
    libraries: ['places']
  });

  useEffect(() => {
    if (isLoaded) {
      loadGoogleMapsScript({ apiKey: "AIzaSyAklz8Mhdgjy3jrxLjp6BJUZE83yYFR8PY" }).then((maps) => {
        autocompleteService.current = new maps.places.AutocompleteService();
        geocoderService.current = new maps.Geocoder();
      });
    }
  }, [isLoaded]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(LOCATION_LIST_API, {
        headers: getAdminAuthHeader(),
      });
      setDataList(res.data?.data || []);
    } catch (err) {
      console.error('Error fetching locations', err);
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleShow = () => {
    setFormData({ location: '', latitude: '', longitude: '' });
    setEditId(null);
    setShowCanvas(true);
    setSuggestions([]);
    setShowSuggestions(false);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 100);
  };

  const handleClose = () => {
    setShowCanvas(false);
    setFormData({ location: '', latitude: '', longitude: '' });
    setEditId(null);
    setSelectedLocation(null);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const onMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
    
    setSelectedLocation({
      lat: lat,
      lng: lng
    });
    
    // Reverse geocode to get address
    if (geocoderService.current) {
      geocoderService.current.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results[0]) {
          setFormData(prev => ({
            ...prev,
            location: results[0].formatted_address
          }));
        }
      });
    }
  }, []);

  const onMapLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, location: value }));
    
    if (!value || !autocompleteService.current) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    autocompleteService.current.getPlacePredictions(
      { input: value, types: ['geocode'] },
      (results, status) => {
        if (status === "OK") {
          setSuggestions(results || []);
          setShowSuggestions(results && results.length > 0);
        }
      }
    );
  };

  const handleSelectSuggestion = (place) => {
    geocoderService.current.geocode({ placeId: place.place_id }, (results, status) => {
      if (status === "OK" && results[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        const lat = loc.lat();
        const lng = loc.lng();
        
        setFormData({
          location: results[0].formatted_address,
          latitude: lat,
          longitude: lng
        });
        
        setSelectedLocation({ lat, lng });
        
        // Pan map to selected location
        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(15);
        }
      }
    });
    
    setShowSuggestions(false);
  };

  const handleToggleStatus = async (id) => {
    try {
      await axios.patch(`${LOCATION_API_BASE}/changeStatus/${id}`, {}, {
        headers: getAdminAuthHeader(),
      });
      toast.success('Status updated successfully!');
      await fetchLocations();
    } catch (err) {
      console.error('Error toggling status', err);
      toast.error('Failed to update status');
    }
  };

  const handleAddOrUpdate = async () => {
    try {
      if (!formData.location) {
        return toast.error('Please enter a location');
      }

      if (editId) {
        await axios.put(`${LOCATION_API_BASE}/updateLocation/${editId}`, formData, {
          headers: getAdminAuthHeader(),
        });
        toast.success('Location updated successfully!');
      } else {
        await axios.post(`${LOCATION_API_BASE}/addLocation`, formData, {
          headers: getAdminAuthHeader(),
        });
        toast.success('Location added successfully!');
      }
      await fetchLocations();
      handleClose();
    } catch (err) {
      console.error('Error saving location', err);
      toast.error(err.response?.data?.message || 'Failed to save location');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      location: item.location,
      latitude: item.latitude || '',
      longitude: item.longitude || ''
    });
    setEditId(item._id);
    setSelectedLocation(item.latitude && item.longitude ? {
      lat: parseFloat(item.latitude),
      lng: parseFloat(item.longitude)
    } : null);
    setShowCanvas(true);
  };

  const confirmDelete = (item) => {
    setItemToDelete(item._id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${LOCATION_API_BASE}/deleteLocation/${itemToDelete}`, {
        headers: getAdminAuthHeader(),
      });
      toast.success('Location deleted successfully!');
      await fetchLocations();
    } catch (err) {
      console.error('Error deleting location', err);
      toast.error('Failed to delete location');
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Location Management
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <GeoAltFill size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
              Location Management
            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              {dataList.length} total locations
            </Typography>
          </Box>
        </Box>
        
        <Button
          onClick={handleShow}
          className="d-flex align-items-center gap-2 fw-bold"
          style={{
            background: primaryGradient,
            border: "none",
            borderRadius: "10px",
            padding: "10px 20px",
            boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
          }}
        >
          <PlusCircleFill size={16} /> Add Location
        </Button>
      </Box>

      {/* Table */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            py={8}
          >
            <Spinner
              animation="border"
              style={{
                color: "#0d47a1",
                width: 36,
                height: 36,
                borderWidth: 3,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Loading locations...
            </Typography>
          </Box>
        ) : (
          <Table hover className="mb-0 align-middle">
            <thead style={{ background: primaryGradient }}>
              <tr>
                {["#", "Location", "Status", "Actions"].map((h) => (
                  <th
                    key={h}
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "13px 16px",
                      border: "none",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataList.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-5 text-muted">
                    No locations found.
                  </td>
                </tr>
              ) : (
                dataList.map((item, index) => (
                  <tr
                    key={item._id}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {index + 1}
                    </td>
                    <td style={{ fontWeight: 500, color: "#1a237e" }}>
                      {item.location}
                    </td>
                    <td>
                      <span
                        className={`badge px-3 py-2 rounded-pill ${
                          item.status === 'Active' ? 'bg-success' : 'bg-danger'
                        }`}
                        style={{ fontSize: '0.75rem' }}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <ActionBtn
                          tooltip="Edit Location"
                          color="#f57f17"
                          onClick={() => handleEdit(item)}
                        >
                          <PencilSquare size={16} />
                        </ActionBtn>

                        <ActionBtn
                          tooltip="Delete Location"
                          color="#c62828"
                          onClick={() => confirmDelete(item)}
                        >
                          <Trash size={16} />
                        </ActionBtn>

                        <ActionBtn
                          tooltip={item.status === 'Active' ? 'Block Location' : 'Activate Location'}
                          color={item.status === 'Active' ? "#c62828" : "#2e7d32"}
                          onClick={() => handleToggleStatus(item._id)}
                        >
                          {item.status === 'Active' ? (
                            <XCircleFill size={17} />
                          ) : (
                            <CheckCircleFill size={17} />
                          )}
                        </ActionBtn>
                      </Box>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </Box>

      {/* Offcanvas Form */}
      <Offcanvas 
        show={showCanvas} 
        onHide={handleClose} 
        placement="end"
        style={{ width: 480 }}
      >
        <Offcanvas.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Offcanvas.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "1rem" }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "7px",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {editId ? <PencilSquare size={13} color="#fff" /> : <PlusCircleFill size={13} color="#fff" />}
            </Box>
            {editId ? 'Edit Location' : 'Add Location'}
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body style={{ background: "#f7f9ff" }}>
          <Box sx={{ p: 2, background: "#fff", borderRadius: "12px", border: "1px solid #e4eaf8" }}>
            <Form>
              {/* Map Section */}
              {isLoaded ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" sx={{ ...labelStyle, mb: 1, display: 'block' }}>
                    Click on map to select location
                  </Typography>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={selectedLocation || center}
                    zoom={selectedLocation ? 15 : 5}
                    onClick={onMapClick}
                    onLoad={onMapLoad}
                    options={{
                      mapTypeControl: false,
                      streetViewControl: false,
                      fullscreenControl: true,
                    }}
                  >
                    {selectedLocation && <Marker position={selectedLocation} />}
                  </GoogleMap>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4, background: '#f0f4ff', borderRadius: '8px', mb: 3 }}>
                  <Spinner animation="border" style={{ color: '#0d47a1' }} />
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#7b8ab8' }}>
                    Loading map...
                  </Typography>
                </Box>
              )}

              {/* Location Input */}
              <Form.Group className="mb-4" style={{ position: 'relative' }}>
                <Form.Label style={labelStyle}>
                  Location <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  style={fieldStyle}
                  value={formData.location}
                  onChange={handleAddressChange}
                  placeholder="Search for a location"
                  ref={inputRef}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      zIndex: 1000,
                      width: '100%',
                      backgroundColor: '#fff',
                      border: '1px solid #dde4f5',
                      borderRadius: '8px',
                      mt: 0.5,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      boxShadow: '0 4px 12px rgba(13,71,161,0.15)',
                    }}
                  >
                    {suggestions.map((place) => (
                      <Box
                        key={place.place_id}
                        onClick={() => handleSelectSuggestion(place)}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f4ff',
                          fontSize: '0.8rem',
                          color: '#1a237e',
                          '&:hover': {
                            backgroundColor: '#f0f4ff',
                          },
                        }}
                      >
                        <Search size={12} style={{ marginRight: '8px', color: '#7b8ab8' }} />
                        {place.description}
                      </Box>
                    ))}
                  </Box>
                )}
              </Form.Group>

              {/* Coordinates Display */}
              {formData.latitude && formData.longitude && (
                <Box
                  sx={{
                    background: '#f0f4ff',
                    borderRadius: '8px',
                    p: 1.5,
                    mb: 3,
                    border: '1px solid #dde4f5',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#1a237e', fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Selected Coordinates
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6b7a99' }}>
                    Lat: {formData.latitude.toFixed(6)} | Lng: {formData.longitude.toFixed(6)}
                  </Typography>
                </Box>
              )}

              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={handleClose}
                  style={{ flex: 1, borderRadius: '8px', borderColor: '#dde4f5' }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleAddOrUpdate}
                  style={{
                    flex: 1,
                    background: primaryGradient,
                    border: 'none',
                    borderRadius: '8px',
                  }}
                  disabled={!formData.location || !formData.latitude || !formData.longitude}
                >
                  {editId ? 'Update Location' : 'Save Location'}
                </Button>
              </div>
            </Form>
          </Box>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <Trash size={18} /> Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: '1.5rem' }}>
          <Box
            sx={{
              background: "#fff3e0",
              border: "1px solid #ffe0b2",
              borderRadius: "8px",
              p: 2,
              fontSize: "0.9rem",
              color: "#e65100",
            }}
          >
            ⚠ Are you sure you want to delete this location? This action cannot be undone.
          </Box>
        </Modal.Body>
        <Modal.Footer style={{ background: "#f7f9ff", borderTop: '1px solid #e4eaf8' }}>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteModal(false)}
            style={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            style={{ borderRadius: '8px' }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default Data;
